import React, {useEffect, useState, useCallback, useRef} from 'react';
import {Outlet, useNavigate, useLocation} from 'react-router-dom';
import {useCookies} from 'react-cookie';
import LogoutWarningDialog from './LogoutWarningDialog';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import Header from './Header';
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import {alpha} from "@mui/material/styles";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const getPageName = (pathname) => {
    switch (pathname) {
        case '/':
            return 'AccountOverview';
        case '/transaction':
            return 'Transaction';
        case '/transaction/check':
            return 'TransactionCheck';
        default:
            return 'unknown_page';
    }
};

export default function ProtectedLayout() {
    const [cookies, setCookie, removeCookie] = useCookies(['session-token']);
    const navigate = useNavigate();
    const location = useLocation();

    // State for the inactivity timer
    const [sessionConfig, setSessionConfig] = useState(null);
    const [logoutWarningIsOpen, setLogoutWarningIsOpen] = useState(false);
    const inactivityTimer = useRef(null);

    const handleLogout = useCallback((type) => {
        const token = cookies['session-token'];
        if (token) {
            fetch('/api/logout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session: token, type: type })
            });
        }
        removeCookie('session-token', { path: '/' });
        navigate('/sign-in');
    }, [cookies, removeCookie, navigate]);

    // --- Inactivity Timer Logic ---

    // Function to reset the timer
    const resetInactivityTimer = useCallback(() => {
        // Clear the existing timer
        if (inactivityTimer.current) {
            clearTimeout(inactivityTimer.current);
        }

        // Only set a new timer if we have the config and the feature is enabled
        if (sessionConfig && sessionConfig.show_popup && sessionConfig.initial_inactivity_seconds > 0) {
            inactivityTimer.current = setTimeout(() => {
                setLogoutWarningIsOpen(true);
                // Log that the dialog has opened
                fetch('/api/timeout_dialog_open', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({session: cookies['session-token']})
                });
            }, sessionConfig.initial_inactivity_seconds * 1000);
        }
    }, [sessionConfig, cookies]);

    // Fetch session configuration on mount
    useEffect(() => {
        const token = cookies['session-token'];
        if (!token) {
            navigate('/sign-in');
            return;
        }

        fetch('/api/session_info', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({session: token}),
        })
            .then(res => {
                if (!res.ok) {
                    // If session is invalid, logout
                    if (res.status === 401) {
                        removeCookie('session-token', {path: '/'});
                        navigate('/sign-in');
                    }
                    throw new Error('Failed to fetch session info');
                }
                return res.json();
            })
            .then(data => {
                setSessionConfig(data);
            })
            .catch(err => console.error(err));

    }, [cookies, navigate, removeCookie]);

    // Effect to start and reset the timer once config is loaded
    useEffect(() => {
        resetInactivityTimer();
        // Cleanup on unmount
        return () => {
            if (inactivityTimer.current) {
                clearTimeout(inactivityTimer.current);
            }
        };
    }, [sessionConfig, resetInactivityTimer]);

    // Effect to handle resets on navigation
    useEffect(() => {
        if (sessionConfig && sessionConfig.reset_inactivity_on_navigation) {
            resetInactivityTimer();
        }
    }, [location, sessionConfig, resetInactivityTimer]);

    // Effect to handle resets on keystroke
    useEffect(() => {
        const handleKeyDown = () => {
            if (sessionConfig && sessionConfig.reset_inactivity_on_keystroke) {
                resetInactivityTimer();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [sessionConfig, resetInactivityTimer]);

    const handleTimeoutDialogClose = () => {
        setLogoutWarningIsOpen(false);
        resetInactivityTimer(); // Reset the timer as soon as the user interacts with the dialog
        // Log that the dialog has been closed
        fetch('/api/timeout_dialog_close', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({session: cookies['session-token']})
        });
    };

    return (
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'left',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header onLogout={() => handleLogout('header button')} />
            <Outlet />
          </Stack>

            <Dialog
                open={logoutWarningIsOpen}
                TransitionComponent={Transition}
                keepMounted
                onClose={(event, reason) => {
                    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
                    handleTimeoutDialogClose();
                }}
                sx={{ zIndex: 9999 }}
                aria-describedby="alert-dialog-slide-description"
            >
                <LogoutWarningDialog
                    isOpen={logoutWarningIsOpen}
                    handleClose={handleTimeoutDialogClose}
                    handleLogout={() => handleLogout('dialog button')}
                    handleTimeout={() => handleLogout(`timeout in ${getPageName(location.pathname)}`)}
                    countdownSeconds={sessionConfig ? sessionConfig.dialog_countdown_seconds : 60}
                />
            </Dialog>
        </Box>
    );
}
